import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ImageBox } from '../../../components/BoxedImage';
import { ImageViewer } from '../../../components/ImageViewer';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import type { BBox } from '../../../types';
import { BucketChooserOverlay } from '../../components/common/BucketChooserOverlay';
import { ConfidenceMatrixInfo } from '../../components/common/ConfidenceMatrixInfo';
import { DetectionCropPreview } from '../../components/common/DetectionCropPreview';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import { OutcomeDistributionBar } from '../../components/common/OutcomeDistributionBar';
import {
  ReviewQueueDatePicker,
  type ReviewQueueDateMode,
} from '../../components/common/ReviewQueueDatePicker';
import { DEFAULT_FORGE_CHAIN_ID, FORGE_CHAIN_OPTIONS } from '../../config/chains';
import {
  bucketLabel,
  bucketPrefix,
  isValidBucketPath,
  RECOGNITION_CELL_LABELS,
  RECOGNITION_CELL_ORDER,
  RECOGNITION_CELL_TONE,
  RECOGNITION_CONFIDENCE_PROFILE_LABEL,
  RECOGNITION_OTHER_BUCKET,
} from '../../constants/recognitionReview';
import {
  enumParam,
  nullableStringParam,
  numberParam,
  pageParam,
  useUrlFilterState,
} from '../../hooks/useUrlFilterState';
import { FORGE_RECOGNITION_OKRS_PATH } from '../../paths';
import { ForgeApiService } from '../../services/ForgeApiService';
import type {
  RecognitionCaptureUnitRow,
  RecognitionGroundTruthKind,
  RecognitionMatchState,
  RecognitionOutcomeCell,
  RecognitionReviewCandidate,
  RecognitionReviewOutcome,
  RecognitionReviewQueueFilters,
  RecognitionReviewQueueSort,
  RecognitionReviewRequest,
  RecognitionReviewSampleDetail,
  RecognitionReviewUnitDetailResponse,
  RecognitionSampleRecognition,
  RecognitionSamplingStrategy,
  RecognitionSubmittedIdentityStatus,
} from '../../types';
import { buildMasterLabelUrl } from '../../utils/masterLabelLinks';
import { isDateInWeek, lastCompletedWeekStart } from '../../utils/recognitionWeeks';
import { safeLocalStorageGetItem, safeLocalStorageSetItem } from '../../utils/safeStorage';

const QUEUE_PAGE_LIMIT = 50;

/** Overlay render settings, persisted per reviewer the way Remote DC persists its own. */
const STORAGE_KEYS = {
  polygonStrokeWidth: 'forge-recognition-review-polygon-stroke-width',
  overlayOpacityPct: 'forge-recognition-review-overlay-opacity-pct',
};

const POLYGON_STROKE_RANGE = { min: 1, max: 32 };
const OVERLAY_OPACITY_RANGE = { min: 0, max: 100 };
const DEFAULT_POLYGON_STROKE_WIDTH = 10;
const DEFAULT_OVERLAY_OPACITY_PCT = 20;
/** How much a reviewed polygon's fill dims relative to an unreviewed one at the same setting. */
export const REVIEWED_FILL_SCALE = 0.36;
/** Extra stroke the selected polygon takes on top of the reviewer's border setting. */
const SELECTED_STROKE_BONUS = 4;

/** A product this young has had little chance to accumulate embedding crops. */
const NEW_PRODUCT_AGE_DAYS = 14;
/** Mirrors the backend `_NON_FRONT_ORIENTATIONS`; both spellings occur in stored evidence. */
const NON_FRONT_ORIENTATIONS = new Set(['not-front', 'not_front']);
/** Everything the inspector modal's focus trap treats as a stop; matches the Remote DC panels. */
const FOCUSABLE_SELECTOR =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

const SETTABLE_SAMPLING_STRATEGIES: readonly RecognitionSamplingStrategy[] = [
  'untagged',
  'representative',
  'targeted',
];
const SAMPLING_STRATEGY_LABELS: Record<RecognitionSamplingStrategy, string> = {
  untagged: 'Untagged',
  representative: 'Representative',
  targeted: 'Targeted',
};

const GROUND_TRUTH_KIND_LABELS: Record<RecognitionGroundTruthKind, string> = {
  catalog_item: 'Catalog item',
  unresolved_product: 'Unresolved product',
  non_product: 'Non-product',
};

/**
 * `scan_unresolved` is not "no scan": the collector did submit a barcode, it just did not resolve
 * to a catalog item. The label has to say that, because the remedy is a lookup rather than a rescan.
 */
const SUBMITTED_IDENTITY_STATUS_LABELS: Record<RecognitionSubmittedIdentityStatus, string> = {
  unlabeled: 'No scan submitted',
  scanned: 'Scanned',
  scan_unresolved: 'Scan did not resolve',
};

/**
 * `mod` names the OPEN unit (it is half of the unit key, alongside `image`); `tag` is the queue's
 * mod-tag filter. They are separate params because opening a unit must not narrow the queue, and
 * filtering the queue must not close the unit being reviewed.
 *
 * `reviewed` drives the backend's `hide_reviewed`, spelled as what the queue SHOWS rather than as
 * what it hides, so the URL reads the same way the toggle does. `pending` is the default and stays
 * out of the URL.
 */
const RECOGNITION_REVIEW_URL_FILTERS = {
  chain: numberParam(DEFAULT_FORGE_CHAIN_ID),
  week: nullableStringParam(),
  image: nullableStringParam(),
  mod: nullableStringParam(),
  sample: nullableStringParam(),
  tag: nullableStringParam(),
  collector: nullableStringParam(),
  captured: nullableStringParam(),
  capturedTo: nullableStringParam(),
  dateMode: enumParam(['day', 'range'] as const, 'week'),
  sort: enumParam(['triage_asc'] as const, 'collected_desc'),
  reviewed: enumParam(['all'] as const, 'pending'),
  page: pageParam(),
};

// ===== Pure helpers (exported so they can be unit-tested without rendering the page) =====

function readStoredNumber(key: string, fallback: number, min: number, max: number): number {
  const stored = safeLocalStorageGetItem(key);
  if (!stored) return fallback;
  const parsed = Number(stored);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

export type RecognitionBoxTone = 'correct' | 'adjusted' | 'incorrect' | 'unscoreable';

/**
 * Overlay colours for the four box tones. These paint SVG strokes over a photograph rather than a
 * themed surface, so they are fixed hex values - the same severity palette the Forge dashboards use
 * - and stay legible in every Forge theme.
 */
export const RECOGNITION_BOX_COLORS: Record<RecognitionBoxTone, string> = {
  correct: '#41c35b',
  adjusted: '#eea337',
  incorrect: '#e8523f',
  unscoreable: 'rgba(148, 163, 184, 0.9)',
};

const RECOGNITION_PRODUCT_BORDER_COLOR = '#2f81f7';
const RECOGNITION_CARTON_BORDER_COLOR = '#a855f7';
type RecognitionDetectionVisibility = 'product' | 'carton' | 'all';

/** Long dashes: nothing was submitted for this polygon at all. */
const UNLABELED_DASH_ARRAY = '18 12';
/** Fine dots: a scan exists but never reached a catalog item - a different, lesser gap. */
const UNRESOLVED_SCAN_DASH_ARRAY = '3 7';

export interface RecognitionBoxVisual {
  tone: RecognitionBoxTone;
  color: string;
  /** No scan was submitted for this polygon. */
  dashed: boolean;
  /** A scan WAS submitted but did not resolve to a catalog item. */
  unresolvedScan: boolean;
  /** Stroke dash pattern for the polygon, or `null` for the solid stroke a scanned polygon gets. */
  strokeDasharray: string | null;
  /** A reviewer has recorded a verdict; the box dims and takes a check badge. */
  reviewed: boolean;
  /** The recognition was below the confidence threshold; the box takes a `?` marker. */
  unconfident: boolean;
  badge: string;
  /**
   * Multiplier on the reviewer's fill-opacity setting, not an absolute opacity: the slider owns how
   * strong the overlay is, and this only says how much a reviewed polygon recedes behind it.
   */
  fillScale: number;
}

/**
 * Translate one sample's current best knowledge into its polygon's visual state.
 *
 * The reviewed outcome supersedes the raw one the moment it exists, so a box always shows what is
 * known now rather than what production predicted. An unscoreable sample has no match state to
 * colour by - that is an evaluation-coverage failure, not an incorrect recognition - so it renders
 * grey.
 *
 * The stroke says what the collector submitted, and the three submitted-identity states are three
 * distinct things: a solid stroke is a resolved scan, long dashes mean nothing was submitted, and
 * fine dots mean a barcode was scanned but did not resolve to a catalog item. Collapsing the last
 * two into one "no scan" dash would tell the reviewer to go find a scan that already exists.
 *
 * @param sample One sample of the loaded capture unit.
 * @returns The colour, stroke, dim, and badge decisions for the sample's polygon.
 */
export function recognitionBoxVisual(sample: RecognitionReviewSampleDetail): RecognitionBoxVisual {
  const outcome = sample.reviewedOutcome ?? sample.rawOutcome;
  const reviewed = sample.review.status === 'reviewed';
  const dashed = sample.submittedIdentity.status === 'unlabeled';
  const unresolvedScan = sample.submittedIdentity.status === 'scan_unresolved';
  const unconfident = outcome.confident === false;
  const tone: RecognitionBoxTone =
    outcome.scoreable && outcome.matchState ? outcome.matchState : 'unscoreable';
  return {
    tone,
    color: RECOGNITION_BOX_COLORS[tone],
    dashed,
    unresolvedScan,
    strokeDasharray: dashed
      ? UNLABELED_DASH_ARRAY
      : unresolvedScan
        ? UNRESOLVED_SCAN_DASH_ARRAY
        : null,
    reviewed,
    unconfident,
    badge: `${reviewed ? '✓' : ''}${unconfident ? '?' : ''}`,
    fillScale: reviewed ? REVIEWED_FILL_SCALE : 1,
  };
}

/** Mirror of the backend's usable-evidence gate; decides whether a verdict can be scored at all. */
export function hasUsableRecognitionEvidence(recognition: RecognitionSampleRecognition): boolean {
  if (recognition.resultLinkStatus !== 'linked') return false;
  if (recognition.candidates.length === 0) return false;
  return (
    recognition.designLinkEvidenceStatus !== 'failed' &&
    recognition.designLinkEvidenceStatus !== 'skipped'
  );
}

export function topRecognitionCandidate(
  candidates: readonly RecognitionReviewCandidate[]
): RecognitionReviewCandidate | null {
  return candidates.find((candidate) => candidate.isTopCandidate) ?? candidates[0] ?? null;
}

/**
 * Whether the candidate's match went through non-front design-link evidence.
 *
 * The backend reads exactly one value here - the orientation of the candidate's frozen design-link
 * entry - so the tool reads the same field. Scanning `referenceCrops` instead would call a match
 * non-front whenever ANY crop that put the candidate in front happened to be non-front, which
 * disagrees with the server on every mixed-orientation candidate and shows an `adjusted` prediction
 * for a verdict the server would reject as `incorrect`.
 */
export function isNonFrontEvidence(candidate: RecognitionReviewCandidate): boolean {
  return NON_FRONT_ORIENTATIONS.has(String(candidate.designLinkOrientation));
}

export interface VerifiedIdentityDraft {
  kind: RecognitionGroundTruthKind | null;
  normalizedBarcode: string | null;
  itemNumber: string | null;
  productName: string | null;
  primaryBucket: string | null;
  note: string;
}

/**
 * Predict the match state the backend will compute for a verdict, or `null` when it will not score.
 *
 * This mirrors `_match_state` in `fsim/common/product_recognition/review_outcome.py` so the reviewer
 * learns a primary bucket is required before the request goes out. It is a UX pre-check only: the
 * stored outcome always comes back from the save response.
 */
export function predictReviewMatchState(
  sample: RecognitionReviewSampleDetail,
  verified: Pick<VerifiedIdentityDraft, 'kind' | 'normalizedBarcode' | 'itemNumber'>
): RecognitionMatchState | null {
  if (!verified.kind) return null;
  if (!hasUsableRecognitionEvidence(sample.recognition)) return null;
  const top = topRecognitionCandidate(sample.recognition.candidates);
  if (!top) return null;
  if (verified.kind !== 'catalog_item') return 'incorrect';
  if (verified.itemNumber && top.itemNumber && verified.itemNumber === top.itemNumber) {
    return 'correct';
  }
  const barcode = verified.normalizedBarcode;
  if (!barcode) return 'incorrect';
  if (top.groupLinks.full.includes(barcode)) return 'correct';
  if (top.groupLinks.partial.includes(barcode)) return 'adjusted';
  if (isNonFrontEvidence(top) && top.groupLinks.nonFront.includes(barcode)) return 'adjusted';
  return 'incorrect';
}

/** The buckets a verdict will actually carry, plus the ones the computed outcome removed. */
export interface EffectiveReviewBuckets {
  primaryBucket: string | null;
  /** Bucket path dropped because the verdict now scores confident correct; `[]` when none was. */
  dropped: readonly string[];
}

/**
 * Resolve the buckets to send for a verdict against the outcome the backend will compute for it.
 *
 * A bucket names the cause of an outcome other than confident correct, so the backend rejects any
 * bucket on a confident correct verdict. That is a trap for a re-review: the tool hydrates stored
 * Dropping the primary bucket here rather than refusing the save keeps the reviewer's verdict
 * reachable and matches what the backend stores; `dropped` tells the panel what changed.
 *
 * @param sample The sample being reviewed, for its confidence and candidates.
 * @param draft The reviewer's current verdict draft.
 * @returns The primary bucket to send, and anything the outcome removed.
 */
export function effectiveReviewBuckets(
  sample: RecognitionReviewSampleDetail,
  draft: VerifiedIdentityDraft
): EffectiveReviewBuckets {
  const stored = draft.primaryBucket ? [draft.primaryBucket] : [];
  const isConfidentCorrect =
    sample.recognition.confidence.confident === true &&
    predictReviewMatchState(sample, draft) === 'correct';
  if (!isConfidentCorrect) {
    return {
      primaryBucket: draft.primaryBucket,
      dropped: [],
    };
  }
  return { primaryBucket: null, dropped: stored };
}

/**
 * Client-side mirror of the backend `validate_review_request` rules.
 *
 * Validated against the EFFECTIVE buckets, not the draft's: a bucket the confident correct rule
 * already removed cannot also be the reason the verdict is refused.
 *
 * @returns The message to show the reviewer, or `null` when the verdict would be accepted.
 */
export function reviewValidationMessage(
  sample: RecognitionReviewSampleDetail,
  draft: VerifiedIdentityDraft
): string | null {
  if (!draft.kind) return 'Choose a verified identity before saving.';
  if (draft.kind === 'catalog_item' && !draft.normalizedBarcode && !draft.itemNumber) {
    return 'A catalog item verdict needs an item number or a normalized barcode.';
  }
  const { primaryBucket } = effectiveReviewBuckets(sample, draft);
  if (
    primaryBucket &&
    bucketPrefix(primaryBucket) === RECOGNITION_OTHER_BUCKET &&
    !draft.note.trim()
  ) {
    return "An 'Other' primary bucket requires a note.";
  }
  const isConfidentCorrect =
    sample.recognition.confidence.confident === true &&
    predictReviewMatchState(sample, draft) === 'correct';
  if (!isConfidentCorrect && !primaryBucket) {
    return "Choose both an issue category and a specific cause. Use “I don't know” if needed.";
  }
  if (primaryBucket && !isValidBucketPath(primaryBucket)) {
    return "Choose a specific cause under the issue category. Use “I don't know” if needed.";
  }
  return null;
}

export function sampleIsReviewed(sample: RecognitionReviewSampleDetail): boolean {
  return sample.review.status === 'reviewed';
}

export function unitKey(imageId: number, modTag: string | null): string {
  return `${imageId}:${modTag ?? ''}`;
}

/**
 * Whether every polygon of a capture unit has a stored verdict.
 *
 * Styling only. Queue ORDER is entirely the backend's - mean triage score, unscored units last,
 * then unit key - and the tool never re-sorts a page it was handed: a client-side "unreviewed
 * first" pass would reorder rows the server paginated, so page 2 would not continue page 1.
 */
export function unitIsComplete(unit: RecognitionCaptureUnitRow): boolean {
  return unit.total > 0 && unit.reviewed >= unit.total;
}

/** One outcome view of the loaded unit: all six cells zero-filled, plus its coverage failures. */
export interface RecognitionUnitOutcomeStats {
  cells: Record<RecognitionOutcomeCell, number>;
  /** Samples that carry an outcome this view could score. */
  scoreable: number;
  /** Samples this view could NOT score - an evaluation-coverage failure, not a wrong answer. */
  coverageFailures: number;
}

export interface RecognitionUnitStats {
  total: number;
  reviewed: number;
  /**
   * Polygons whose submitted identity resolved to a catalog item, matching the queue row's
   * `scanned`. A barcode that did not resolve is NOT collected: it is a scan issue.
   */
  scanned: number;
  /**
   * Unresolved scans plus reviewed samples scored as a wrong scan, matching the queue row's
   * `scanIssues`. A polygon can be counted here and in `scanned` only via the wrong-scan half.
   */
  scanIssues: number;
  /** What production predicted, over every sample in the unit. */
  raw: RecognitionUnitOutcomeStats;
  /** Stored reviewer verdicts, over the reviewed samples only. */
  reviewedView: RecognitionUnitOutcomeStats;
}

function emptyCellCounts(): Record<RecognitionOutcomeCell, number> {
  return RECOGNITION_CELL_ORDER.reduce(
    (counts, cell) => ({ ...counts, [cell]: 0 }),
    {} as Record<RecognitionOutcomeCell, number>
  );
}

/**
 * Summarize the loaded capture unit for the statistics panel.
 *
 * Both views are counted over the SAME sample list but with different denominators: the raw view
 * covers every sample, because production predicted something for all of them, while the reviewed
 * view covers only the samples a reviewer has actually ruled on. Reading the reviewed view as a
 * share of the whole unit would make an unreviewed unit look like a wall of coverage failures.
 *
 * All six cells are zero-filled so the panel's shape does not change as verdicts land, and an
 * unscoreable outcome is counted as a coverage failure rather than dropped: it is the reason the
 * denominator moved, and hiding it would make the numbers look like they do not add up.
 *
 * `scanned` and `scanIssues` restate the queue row's own definitions rather than inventing softer
 * ones, so the panel and the row a reviewer clicked through can never disagree: only a resolved
 * `scanned` status counts as collected, and an unresolved scan is a scan issue.
 *
 * @param samples Every sample of the loaded unit.
 * @returns Review progress, collected-vs-existing, scan issues, and both outcome views.
 */
export function unitStats(samples: readonly RecognitionReviewSampleDetail[]): RecognitionUnitStats {
  const raw: RecognitionUnitOutcomeStats = {
    cells: emptyCellCounts(),
    scoreable: 0,
    coverageFailures: 0,
  };
  const reviewedView: RecognitionUnitOutcomeStats = {
    cells: emptyCellCounts(),
    scoreable: 0,
    coverageFailures: 0,
  };
  let reviewed = 0;
  let scanned = 0;
  let scanIssues = 0;

  for (const sample of samples) {
    if (sample.submittedIdentity.status === 'scanned') scanned += 1;
    else if (sample.submittedIdentity.status === 'scan_unresolved') scanIssues += 1;
    const isReviewed = sampleIsReviewed(sample);
    if (isReviewed) reviewed += 1;
    if (isReviewed && sample.reviewedOutcome?.adjustmentReasons.includes('wrong_scan')) {
      scanIssues += 1;
    }

    const tally = (view: RecognitionUnitOutcomeStats, outcome: RecognitionReviewOutcome): void => {
      if (outcome.scoreable && outcome.cell) {
        view.cells[outcome.cell] += 1;
        view.scoreable += 1;
      } else {
        view.coverageFailures += 1;
      }
    };

    tally(raw, sample.rawOutcome);
    if (isReviewed && sample.reviewedOutcome) tally(reviewedView, sample.reviewedOutcome);
    else if (isReviewed) reviewedView.coverageFailures += 1;
  }

  return { total: samples.length, reviewed, scanned, scanIssues, raw, reviewedView };
}

/** The three queue filters, in the order their chips are painted. */
export const QUEUE_FILTER_KEYS = ['modTag', 'collector', 'capturedDate'] as const;
export type QueueFilterKey = (typeof QUEUE_FILTER_KEYS)[number];

export const QUEUE_FILTER_LABELS: Record<QueueFilterKey, string> = {
  modTag: 'Mod tag',
  collector: 'Collector',
  capturedDate: 'Captured',
};

/**
 * Name the filters the reviewer asked for that were NOT applied to the queue.
 *
 * Two sources, because the two failure modes are different. `clientDropped` names a filter this
 * page refused to send: an out-of-week `captured_date` is rejected by api-dl with a 400 that fails
 * the WHOLE queue request, so it can never be a filter the server quietly ignores - the client is
 * the authority and drops it before asking. `applied` is the backend's echo, which still catches
 * anything else it chose not to apply; the block always carries every unset filter as `null`, so a
 * response missing it entirely is treated as "no echo available" rather than as evidence that every
 * filter was dropped.
 *
 * @param requested The filters the reviewer asked for, before any client-side drop.
 * @param applied The backend's `filters` echo, or `null`/`undefined` when it sent none.
 * @param clientDropped Filters this page refused to send at all.
 * @returns Reviewer-facing labels of the dropped filters, in chip order, without repeats.
 */
export function droppedQueueFilters(
  requested: Record<QueueFilterKey, string | null>,
  applied: RecognitionReviewQueueFilters | null | undefined,
  clientDropped: readonly QueueFilterKey[] = []
): readonly string[] {
  return QUEUE_FILTER_KEYS.filter(
    (key) =>
      requested[key] && (clientDropped.includes(key) || (Boolean(applied) && !applied?.[key]))
  ).map((key) => QUEUE_FILTER_LABELS[key]);
}

/** HTTP status api-dl answers a verdict whose `expected_updated_at` no longer matches with. */
const STALE_REVIEW_STATUS = 409;

/**
 * Whether a failed save was rejected because the sample changed under the reviewer.
 *
 * Checked by status rather than by `instanceof ForgeApiError` so the tool does not depend on the
 * error class surviving whatever wrapped it.
 */
export function isStaleReviewError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { status?: unknown }).status === STALE_REVIEW_STATUS
  );
}

/** Told to the reviewer on 409: the verdict is gone, not queued, so it has to be re-entered. */
export const STALE_REVIEW_MESSAGE =
  'Another reviewer saved this polygon first, so your verdict was NOT applied. The sample has been reloaded with their verdict - review it and save again if you still disagree.';

/**
 * Resolve which queue row `[` / `]` should open next.
 *
 * A unit deep-linked from the OKRs dashboard need not be in the queue at all - a different week, or
 * a row filtered out - and then it has no neighbours to step to. Rather than silently landing on
 * `queue[0]` in both directions, an out-of-queue unit enters the queue at whichever END the
 * requested direction points at: forward starts at the top, backward at the bottom.
 *
 * @param queueIndex Position of the open unit in the queue, or `-1` when it is not in the queue.
 * @param delta `-1` for the previous unit, `+1` for the next one.
 * @param queueLength Number of rows currently in the queue.
 * @returns The index to open, or `null` when there is nowhere to step.
 */
export function nextQueueIndex(
  queueIndex: number,
  delta: number,
  queueLength: number
): number | null {
  if (queueLength === 0) return null;
  if (queueIndex < 0) return null;
  const next = queueIndex + delta;
  return next < 0 || next >= queueLength ? null : next;
}

const EMPTY_DRAFT: VerifiedIdentityDraft = {
  kind: null,
  normalizedBarcode: null,
  itemNumber: null,
  productName: null,
  primaryBucket: null,
  note: '',
};

/**
 * Seed a verdict draft from whatever is already stored on the sample.
 *
 * The current model stores exactly one complete two-tier primary bucket.
 */
export function draftFromSample(
  sample: RecognitionReviewSampleDetail | null
): VerifiedIdentityDraft {
  if (!sample || sample.review.status !== 'reviewed') return EMPTY_DRAFT;
  const { review } = sample;
  return {
    kind: review.verifiedKind,
    normalizedBarcode: review.verifiedNormalizedBarcode,
    itemNumber: review.verifiedItemNumber,
    productName: review.verifiedProductName,
    primaryBucket: review.primaryBucket,
    note: review.note ?? '',
  };
}

/**
 * Bounding rectangle of a sample, falling back to the polygon when the bbox is unusable.
 *
 * Every sample in the unit has to be drawable - an unscoreable polygon with no prediction is
 * exactly the one a reviewer needs to find - so a sample that came back with a degenerate or
 * missing bbox is measured from its own points rather than collapsing to an invisible zero-area
 * box at the origin. Only a sample with neither survives as an empty rect.
 *
 * @param sample One sample of the loaded capture unit.
 * @returns `[x, y, width, height]` in source image coordinates.
 */
export function sampleShape(
  sample: Pick<RecognitionReviewSampleDetail, 'bbox' | 'points'>
): [number, number, number, number] {
  const [x1, y1, x2, y2] = sample.bbox.length >= 4 ? sample.bbox : [0, 0, 0, 0];
  const width = Math.abs(x2 - x1);
  const height = Math.abs(y2 - y1);
  if (width > 0 && height > 0) return [Math.min(x1, x2), Math.min(y1, y2), width, height];
  if (sample.points.length === 0) return [0, 0, 0, 0];
  const xs = sample.points.map((point) => point.x);
  const ys = sample.points.map((point) => point.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return [minX, minY, Math.max(...xs) - minX, Math.max(...ys) - minY];
}

/**
 * Build one polygon's overlay box from its visual state and the reviewer's render settings.
 *
 * @param sample The sample to draw.
 * @param visual Colour, stroke, and badge decisions from `recognitionBoxVisual`.
 * @param isSelected Whether this is the polygon the inspector is showing.
 * @param strokeWidth Border thickness in source pixels, from the reviewer's slider.
 * @param overlayOpacity Fill opacity in `0..1`, from the reviewer's slider.
 */
export function toImageBox(
  sample: RecognitionReviewSampleDetail,
  visual: RecognitionBoxVisual,
  isSelected: boolean,
  strokeWidth: number,
  overlayOpacity: number
): ImageBox {
  const fill = overlayOpacity * visual.fillScale;
  const borderColor =
    sample.classificationType === 'carton'
      ? RECOGNITION_CARTON_BORDER_COLOR
      : RECOGNITION_PRODUCT_BORDER_COLOR;
  return {
    shape: sampleShape(sample),
    color: borderColor,
    strokeColor: borderColor,
    fillColor: visual.color,
    strokeWidth: isSelected ? strokeWidth + SELECTED_STROKE_BONUS : strokeWidth,
    opacity: isSelected ? Math.min(1, fill + 0.14) : fill,
    badge: visual.badge || undefined,
    isPolygon: sample.points.length > 0,
    points: sample.points,
    options: visual.strokeDasharray
      ? { boxRect: { strokeDasharray: visual.strokeDasharray } }
      : undefined,
  };
}

/**
 * Name the capture unit's store, given both halves may be absent on the wire.
 *
 * A store id is an identifier, never a quantity, so it is printed verbatim - locale grouping would
 * turn store 12345 into "12,345" and make it un-searchable.
 */
export function captureUnitStoreLabel(storeName: string | null, storeId: number | null): string {
  if (storeName) return storeName;
  return storeId === null ? 'Unknown store' : `Store ${storeId}`;
}

function valueOrDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
}

function formatSimilarity(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value) ? '-' : value.toFixed(3);
}

function SummaryRow({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | number | null | undefined;
  mono?: boolean;
}): React.ReactElement {
  return (
    <div className="mdc-summary-row">
      <span>{label}</span>
      <strong className={mono ? 'mdc-summary-value--mono' : undefined}>{valueOrDash(value)}</strong>
    </div>
  );
}

function GroupLinkChips({
  candidate,
}: {
  candidate: RecognitionReviewCandidate;
}): React.ReactElement | null {
  const scopesFor = (links: RecognitionReviewCandidate['groupLinks']) =>
    [
      { modifier: 'is-full', label: 'Full', barcodes: links.full },
      { modifier: 'is-partial', label: 'Partial', barcodes: links.partial },
      { modifier: 'is-non-front', label: 'Non-front', barcodes: links.nonFront },
    ].filter((scope) => scope.barcodes.length > 0);
  const frozenScopes = scopesFor(candidate.groupLinks);
  const currentScopes = candidate.currentGroupLinks ? scopesFor(candidate.currentGroupLinks) : null;
  return (
    <div className="rr-tool-group-comparison">
      <div className="rr-tool-chips">
        <span className="rr-tool-bucket-label">At inference</span>
        {frozenScopes.length > 0 ? (
          frozenScopes.map((scope) => (
            <span
              key={`frozen-${scope.modifier}`}
              className={`dg-scope-pill ${scope.modifier}`}
              title={`${scope.label} frozen design-group links: ${scope.barcodes.join(', ')}`}
            >
              {scope.label} {scope.barcodes.length}
            </span>
          ))
        ) : (
          <span className="rr-tool-hint">No links</span>
        )}
      </div>
      <div className="rr-tool-chips">
        <span className="rr-tool-bucket-label">Current</span>
        {currentScopes === null ? (
          <span className="rr-tool-hint">Unavailable</span>
        ) : currentScopes.length > 0 ? (
          currentScopes.map((scope) => (
            <span
              key={`current-${scope.modifier}`}
              className={`dg-scope-pill ${scope.modifier}`}
              title={`${scope.label} current design-group links: ${scope.barcodes.join(', ')}`}
            >
              {scope.label} {scope.barcodes.length}
            </span>
          ))
        ) : (
          <span className="rr-tool-hint">No links</span>
        )}
        {candidate.designGroupChangedSinceInference === true ? (
          <span className="dg-scope-pill is-warning">Changed since inference</span>
        ) : candidate.designGroupChangedSinceInference === false ? (
          <span className="dg-scope-pill is-full">Unchanged</span>
        ) : null}
      </div>
    </div>
  );
}

export const RecognitionReviewToolPage: React.FC = () => {
  const { token, hasInternalAccess } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [filters, updateFilters] = useUrlFilterState(RECOGNITION_REVIEW_URL_FILTERS);
  const {
    chain: chainId,
    week: weekFilter,
    image: imageFilter,
    mod: modFilter,
    sample: sampleFilter,
    tag: modTagFilter,
    collector: collectorFilter,
    captured: capturedFilter,
    capturedTo: capturedToFilter,
    dateMode,
    sort: queueSort,
    reviewed: reviewedFilter,
    page,
  } = filters;
  const hideReviewed = reviewedFilter !== 'all';

  const [queue, setQueue] = useState<RecognitionCaptureUnitRow[]>([]);
  const [queueWeek, setQueueWeek] = useState<string | null>(null);
  const [queueTotal, setQueueTotal] = useState(0);
  const [hiddenReviewedUnits, setHiddenReviewedUnits] = useState<number | null>(null);
  const [droppedFilters, setDroppedFilters] = useState<readonly string[]>([]);
  const [isQueueLoading, setIsQueueLoading] = useState(true);
  const [queueError, setQueueError] = useState<string | null>(null);
  // The week the LAST queue response resolved to, held in a ref rather than in `queueWeek` so
  // reading it back cannot re-run `loadQueue` and refetch the page it was just set from.
  const resolvedQueueWeek = useRef<string | null>(null);

  // Free-text filters commit on submit, so the queue is not refetched per keystroke. The mirrors
  // are seeded from the URL and re-seeded whenever it changes underneath them (chip removal, back).
  const [modTagInput, setModTagInput] = useState(modTagFilter ?? '');
  const [collectorInput, setCollectorInput] = useState(collectorFilter ?? '');
  useEffect(() => setModTagInput(modTagFilter ?? ''), [modTagFilter]);
  useEffect(() => setCollectorInput(collectorFilter ?? ''), [collectorFilter]);

  const [unit, setUnit] = useState<RecognitionReviewUnitDetailResponse | null>(null);
  const [isUnitLoading, setIsUnitLoading] = useState(false);
  const [unitError, setUnitError] = useState<string | null>(null);

  // Drafts and the identity lookup are keyed by sample rather than reset from an effect, so moving
  // between polygons never races a keystroke and an in-progress verdict survives a detour.
  const [draftsBySampleId, setDraftsBySampleId] = useState<Record<string, VerifiedIdentityDraft>>(
    {}
  );
  // Overlay render settings survive reloads and unit changes: a reviewer who has found the border
  // and fill that suit their screen should not have to re-find it on the next capture unit.
  const [polygonStrokeWidth, setPolygonStrokeWidth] = useState(() =>
    readStoredNumber(
      STORAGE_KEYS.polygonStrokeWidth,
      DEFAULT_POLYGON_STROKE_WIDTH,
      POLYGON_STROKE_RANGE.min,
      POLYGON_STROKE_RANGE.max
    )
  );
  const [overlayOpacityPct, setOverlayOpacityPct] = useState(() =>
    readStoredNumber(
      STORAGE_KEYS.overlayOpacityPct,
      DEFAULT_OVERLAY_OPACITY_PCT,
      OVERLAY_OPACITY_RANGE.min,
      OVERLAY_OPACITY_RANGE.max
    )
  );

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.polygonStrokeWidth, String(polygonStrokeWidth));
  }, [polygonStrokeWidth]);

  useEffect(() => {
    safeLocalStorageSetItem(STORAGE_KEYS.overlayOpacityPct, String(overlayOpacityPct));
  }, [overlayOpacityPct]);

  const [isSaving, setIsSaving] = useState(false);
  const [isBucketChooserOpen, setIsBucketChooserOpen] = useState(false);
  const [isStrategySaving, setIsStrategySaving] = useState(false);
  const [detectionVisibility, setDetectionVisibility] =
    useState<RecognitionDetectionVisibility>('all');
  // The detail popup starts closed: it covers the capture image, and the image is what the
  // reviewer is reading. It opens on an explicit ask - a polygon click, the Details button, or `I`.
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const [lookup, setLookup] = useState<{
    sampleId: string;
    query: string;
    result: VerifiedIdentityDraft | null;
    error: string | null;
    isLoading: boolean;
  } | null>(null);

  // `missing` and `failed` are separate on purpose: a null `imageUri` is an expected backend
  // degradation the reviewer can only escape by reloading the unit, while a decode failure is worth
  // retrying against the same URI. Neither may masquerade as a spinner that never resolves.
  const [captureImage, setCaptureImage] = useState<
    | { status: 'missing' }
    | { status: 'loading' }
    | { status: 'ready'; element: HTMLImageElement }
    | { status: 'failed' }
  >({ status: 'missing' });
  const [imageRetryCount, setImageRetryCount] = useState(0);

  const queueRequestSequence = useRef(0);
  const unitRequestSequence = useRef(0);

  const imageId = imageFilter ? Number(imageFilter) : null;
  const openUnitKeyRef = useRef<string | null>(null);
  openUnitKeyRef.current =
    imageId === null || !Number.isFinite(imageId) ? null : unitKey(imageId, modFilter);

  const loadQueue = useCallback(async () => {
    const requestSequence = queueRequestSequence.current + 1;
    queueRequestSequence.current = requestSequence;
    setIsQueueLoading(true);
    // The week this request will resolve to: the explicit filter, else the week the last response
    // resolved to, else the backend's own rule (the last completed week). Knowing it matters
    // because api-dl answers a `captured_date` outside the week with a 400 that takes the entire
    // queue down - a stale or hand-edited URL must not be able to do that - so the date is dropped
    // here instead of being sent. The fallback is computed in UTC while the backend resolves the
    // week chain-locally, which can disagree for a few hours at a week boundary; dropping a still
    // valid date for one request is recoverable, a failed queue is not.
    const requestWeek = weekFilter ?? resolvedQueueWeek.current ?? lastCompletedWeekStart();
    const capturedIsInWeek = capturedFilter ? isDateInWeek(capturedFilter, requestWeek) : true;
    const capturedToIsInWeek = capturedToFilter
      ? isDateInWeek(capturedToFilter, requestWeek)
      : true;
    const capturedRangeIsOrdered =
      !capturedFilter || !capturedToFilter || capturedToFilter >= capturedFilter;
    const capturedRangeIsValid = capturedIsInWeek && capturedToIsInWeek && capturedRangeIsOrdered;
    const capturedDate = capturedRangeIsValid ? capturedFilter : null;
    const capturedDateTo = capturedRangeIsValid ? capturedToFilter : null;
    try {
      const response = await ForgeApiService.fetchRecognitionReviewQueue(token, {
        chainId,
        weekStart: weekFilter,
        page,
        limit: QUEUE_PAGE_LIMIT,
        modTag: modTagFilter,
        collector: collectorFilter,
        capturedDate,
        capturedDateTo,
        hideReviewed,
        sort: queueSort as RecognitionReviewQueueSort,
      });
      if (queueRequestSequence.current !== requestSequence) return;
      resolvedQueueWeek.current = response.weekStart;
      setQueue(response.units);
      setQueueWeek(response.weekStart);
      setQueueTotal(response.totalUnits);
      setHiddenReviewedUnits(response.hiddenReviewedUnits ?? null);
      setDroppedFilters(
        droppedQueueFilters(
          { modTag: modTagFilter, collector: collectorFilter, capturedDate: capturedFilter },
          response.filters,
          capturedRangeIsValid ? [] : ['capturedDate']
        )
      );
      setQueueError(null);
    } catch (error) {
      if (queueRequestSequence.current !== requestSequence) return;
      const message =
        error instanceof Error ? error.message : 'Error loading the Recognition review queue';
      setQueue([]);
      setQueueTotal(0);
      setHiddenReviewedUnits(null);
      setDroppedFilters([]);
      setQueueError(message);
      showErrorToast(message);
    } finally {
      if (queueRequestSequence.current === requestSequence) setIsQueueLoading(false);
    }
  }, [
    capturedFilter,
    capturedToFilter,
    chainId,
    collectorFilter,
    hideReviewed,
    modTagFilter,
    page,
    queueSort,
    showErrorToast,
    token,
    weekFilter,
  ]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const loadUnit = useCallback(async () => {
    const requestSequence = unitRequestSequence.current + 1;
    unitRequestSequence.current = requestSequence;
    if (imageId === null || !Number.isFinite(imageId)) {
      setUnit(null);
      setUnitError(null);
      setIsUnitLoading(false);
      return;
    }
    setIsUnitLoading(true);
    try {
      const response = await ForgeApiService.fetchRecognitionReviewUnit(token, {
        chainId,
        imageId,
        modTag: modFilter,
      });
      if (unitRequestSequence.current !== requestSequence) return;
      setUnit(response);
      setUnitError(null);
    } catch (error) {
      if (unitRequestSequence.current !== requestSequence) return;
      const message = error instanceof Error ? error.message : 'Error loading the capture unit';
      setUnit(null);
      setUnitError(message);
      showErrorToast(message);
    } finally {
      if (unitRequestSequence.current === requestSequence) setIsUnitLoading(false);
    }
  }, [chainId, imageId, modFilter, showErrorToast, token]);

  useEffect(() => {
    void loadUnit();
  }, [loadUnit]);

  useEffect(() => {
    const uri = unit?.imageUri;
    if (!uri) {
      setCaptureImage({ status: 'missing' });
      return undefined;
    }
    let cancelled = false;
    setCaptureImage({ status: 'loading' });
    const element = new Image();
    element.onload = () => {
      if (!cancelled) setCaptureImage({ status: 'ready', element });
    };
    element.onerror = () => {
      if (!cancelled) setCaptureImage({ status: 'failed' });
    };
    element.src = uri;
    return () => {
      cancelled = true;
    };
    // `imageRetryCount` re-runs this effect against the same URI when the reviewer retries.
  }, [imageRetryCount, unit?.imageUri]);

  const samples = useMemo(() => unit?.samples ?? [], [unit]);
  const visibleSampleEntries = useMemo(
    () =>
      samples
        .map((sample, sampleIndex) => ({ sample, sampleIndex }))
        .filter(
          ({ sample }) =>
            detectionVisibility === 'all' || sample.classificationType === detectionVisibility
        ),
    [detectionVisibility, samples]
  );

  const selectedIndex = useMemo(() => {
    if (visibleSampleEntries.length === 0) return -1;
    const byFilter = visibleSampleEntries.find(
      ({ sample }) => sample.evaluationSampleId === sampleFilter
    );
    if (byFilter) return byFilter.sampleIndex;
    const firstUnreviewed = visibleSampleEntries.find(({ sample }) => !sampleIsReviewed(sample));
    return firstUnreviewed?.sampleIndex ?? visibleSampleEntries[0].sampleIndex;
  }, [sampleFilter, visibleSampleEntries]);
  const selectedVisibleIndex = useMemo(
    () => visibleSampleEntries.findIndex(({ sampleIndex }) => sampleIndex === selectedIndex),
    [selectedIndex, visibleSampleEntries]
  );

  const selectedSample = selectedIndex >= 0 ? samples[selectedIndex] : null;
  const selectedSampleId = selectedSample?.evaluationSampleId ?? null;

  const draft = useMemo(
    () =>
      (selectedSampleId ? draftsBySampleId[selectedSampleId] : undefined) ??
      draftFromSample(selectedSample),
    [draftsBySampleId, selectedSample, selectedSampleId]
  );

  const updateDraft = useCallback(
    (updater: (current: VerifiedIdentityDraft) => VerifiedIdentityDraft) => {
      if (!selectedSample) return;
      const sampleId = selectedSample.evaluationSampleId;
      setDraftsBySampleId((current) => ({
        ...current,
        [sampleId]: updater(current[sampleId] ?? draftFromSample(selectedSample)),
      }));
    },
    [selectedSample]
  );

  // A lookup belongs to the polygon it was typed for; anything else is stale, not shown.
  const activeLookup = lookup && lookup.sampleId === selectedSampleId ? lookup : null;

  /**
   * Move the selection, and open the detail popup only when the reviewer asked for it.
   *
   * Arrow keys walk the polygons on the image, which is the whole point of the overlay, so they
   * must not throw a modal over it. Clicking a polygon or pressing `I` is the explicit ask.
   */
  const selectSampleAt = useCallback(
    (index: number, shouldOpen = false) => {
      if (visibleSampleEntries.length === 0) return;
      const bounded =
        ((index % visibleSampleEntries.length) + visibleSampleEntries.length) %
        visibleSampleEntries.length;
      updateFilters({ sample: visibleSampleEntries[bounded].sample.evaluationSampleId });
      if (shouldOpen) setIsPanelOpen(true);
    },
    [updateFilters, visibleSampleEntries]
  );

  /** Every queue filter change restarts at page 1; a page 3 of the old filter means nothing. */
  const applyQueueFilter = useCallback(
    (partial: {
      tag?: string | null;
      collector?: string | null;
      captured?: string | null;
      capturedTo?: string | null;
    }) => updateFilters({ ...partial, page: 1 }),
    [updateFilters]
  );

  const openUnit = useCallback(
    (row: RecognitionCaptureUnitRow) => {
      updateFilters({
        image: String(row.imageId),
        mod: row.modTag,
        sample: null,
      });
      setIsPanelOpen(false);
    },
    [updateFilters]
  );

  const queueIndex = useMemo(
    () =>
      imageId === null
        ? -1
        : queue.findIndex(
            (row) => unitKey(row.imageId, row.modTag) === unitKey(imageId, modFilter)
          ),
    [imageId, modFilter, queue]
  );

  const stepUnit = useCallback(
    (delta: number) => {
      const next = nextQueueIndex(queueIndex, delta, queue.length);
      if (next === null) return;
      openUnit(queue[next]);
    },
    [openUnit, queue, queueIndex]
  );

  const applySavedReview = useCallback(
    (
      sampleId: string,
      request: RecognitionReviewRequest,
      outcome: RecognitionReviewSampleDetail['reviewedOutcome'],
      /** The sample's post-write `updatedAt`, or `null` when the save response carried none. */
      updatedAt: string | null,
      /** True when this verdict is the sample's first, so the queue row's progress moved. */
      isFirstVerdict: boolean
    ) => {
      // The queue row is counted server-side, so without this the sidebar would show the pre-save
      // `reviewed/total` and its "complete" styling for the rest of the session.
      if (isFirstVerdict && imageId !== null) {
        const key = unitKey(imageId, modFilter);
        setQueue((current) =>
          current.map((row) =>
            unitKey(row.imageId, row.modTag) === key
              ? { ...row, reviewed: Math.min(row.total, row.reviewed + 1) }
              : row
          )
        );
      }
      // The save response carries only the stored outcome, so the reviewer stamp is reconstructed
      // from the session user and the local clock. Without it a just-saved sample reads "Reviewer -"
      // until the page is reloaded, which looks like the verdict failed to attach to anyone.
      const reviewedAt = new Date().toISOString();
      setUnit((current) => {
        if (!current) return current;
        return {
          ...current,
          samples: current.samples.map((sample) =>
            sample.evaluationSampleId === sampleId
              ? {
                  ...sample,
                  // The write bumped the sample's `updated_at`, so the token this page is holding
                  // is spent. Keeping it would make the reviewer's OWN next save look like someone
                  // else's concurrent write, 409 it, and throw away the draft they just typed.
                  updatedAt: updatedAt ?? sample.updatedAt,
                  reviewedOutcome: outcome,
                  review: {
                    ...sample.review,
                    status: 'reviewed',
                    verifiedKind: request.verified_kind,
                    verifiedNormalizedBarcode: request.verified_normalized_barcode ?? null,
                    verifiedItemNumber: request.verified_item_number ?? null,
                    verifiedProductName: draft.productName,
                    primaryBucket: request.primary_bucket ?? null,
                    note: request.note ?? null,
                    // The save response carries no reviewer identity and the session exposes a
                    // display name, not an email, so leave the stored value alone rather than
                    // stamping a name into an email field. `reviewedByYou` drives the optimistic
                    // label until the next unit fetch brings the server's email.
                    reviewedByEmail: sample.review.reviewedByEmail,
                    reviewedByYou: true,
                    reviewedAt,
                  },
                }
              : sample
          ),
        };
      });
    },
    [draft.productName, imageId, modFilter]
  );

  const handleSave = useCallback(async () => {
    if (!hasInternalAccess || !selectedSample || isSaving) return;
    const validationMessage = reviewValidationMessage(selectedSample, draft);
    if (validationMessage) {
      showErrorToast(validationMessage);
      return;
    }
    // Buckets are resolved against the outcome the verdict will score, so a re-review that turns a
    // previously-incorrect polygon confident correct sheds its stored buckets instead of being
    // refused for carrying one the reviewer cannot see.
    const buckets = effectiveReviewBuckets(selectedSample, draft);
    const request: RecognitionReviewRequest = {
      verified_kind: draft.kind as RecognitionGroundTruthKind,
      verified_normalized_barcode: draft.normalizedBarcode,
      verified_item_number: draft.itemNumber,
      primary_bucket: buckets.primaryBucket,
      note: draft.note.trim() || null,
      // Optimistic concurrency: the sample as this reviewer read it. A null `updatedAt` means the
      // sample has never been written, and the backend treats a token against no stored timestamp
      // as a conflict, so it is omitted rather than sent as null.
      ...(selectedSample.updatedAt ? { expected_updated_at: selectedSample.updatedAt } : {}),
    };
    const sampleId = selectedSample.evaluationSampleId;
    const targetUnitKey =
      imageId === null || !Number.isFinite(imageId) ? null : unitKey(imageId, modFilter);
    const isFirstVerdict = !sampleIsReviewed(selectedSample);
    setIsSaving(true);
    try {
      const response = await ForgeApiService.saveRecognitionReview(token, sampleId, request);
      if (targetUnitKey && openUnitKeyRef.current === targetUnitKey) {
        applySavedReview(
          sampleId,
          request,
          response.outcome,
          response.updatedAt ?? null,
          isFirstVerdict
        );
      }
      showSuccessToast('Recognition review saved');
      if (buckets.dropped.length > 0) {
        showSuccessToast(
          `${buckets.dropped.map(bucketLabel).join(', ')} ${
            buckets.dropped.length === 1 ? 'was' : 'were'
          } cleared: a confident correct recognition carries no issue bucket.`
        );
      }
      // Without a fresh token the stored `updatedAt` is knowingly stale and the reviewer's next
      // save on this sample would 409 against their own write. Reload before Save re-enables, so
      // the window where a second save could be sent with a spent token never opens.
      if (!response.updatedAt && targetUnitKey && openUnitKeyRef.current === targetUnitKey) {
        await loadUnit();
      }
      const nextUnreviewed = visibleSampleEntries.findIndex(
        ({ sample }) => sample.evaluationSampleId !== sampleId && !sampleIsReviewed(sample)
      );
      if (nextUnreviewed >= 0 && targetUnitKey && openUnitKeyRef.current === targetUnitKey) {
        selectSampleAt(nextUnreviewed);
      }
    } catch (error) {
      if (isStaleReviewError(error)) {
        // Never a silent retry: the other reviewer's verdict may be the right one, so the sample is
        // reloaded and the decision handed back rather than this verdict being replayed over theirs.
        setDraftsBySampleId((current) => {
          const { [sampleId]: _discarded, ...rest } = current;
          return rest;
        });
        void loadUnit();
        showErrorToast(STALE_REVIEW_MESSAGE);
      } else {
        showErrorToast(
          error instanceof Error ? error.message : 'Error saving the Recognition review'
        );
      }
    } finally {
      setIsSaving(false);
    }
  }, [
    applySavedReview,
    draft,
    hasInternalAccess,
    imageId,
    isSaving,
    loadUnit,
    modFilter,
    selectSampleAt,
    selectedSample,
    showErrorToast,
    showSuccessToast,
    token,
    visibleSampleEntries,
  ]);

  const handleConfirmScan = useCallback(() => {
    if (!selectedSample) return;
    const { submittedIdentity } = selectedSample;
    if (submittedIdentity.status !== 'scanned') {
      showErrorToast(
        submittedIdentity.status === 'scan_unresolved'
          ? 'This polygon’s scan did not resolve to a catalog item; look the identity up instead.'
          : 'This polygon has no submitted scan to confirm.'
      );
      return;
    }
    updateDraft((current) => ({
      ...current,
      kind: 'catalog_item',
      normalizedBarcode: submittedIdentity.normalizedBarcode,
      itemNumber: submittedIdentity.itemNumber,
      productName: submittedIdentity.productName,
    }));
  }, [selectedSample, showErrorToast, updateDraft]);

  const handleMarkKind = useCallback(
    (kind: RecognitionGroundTruthKind) => {
      updateDraft((current) => ({
        ...current,
        kind,
        normalizedBarcode: null,
        itemNumber: null,
        productName: null,
      }));
    },
    [updateDraft]
  );

  const handleLookup = useCallback(async () => {
    const query = (activeLookup?.query ?? '').trim();
    if (!query || !selectedSampleId) return;
    const sampleId = selectedSampleId;
    setLookup({ sampleId, query, result: null, error: null, isLoading: true });
    try {
      // Digit count cannot tell an item number from a barcode - ASDA item numbers are nine digits,
      // so guessing sent every one of them down the barcode lookup, which zero-padded it into a
      // barcode that resolves to no product at all. Try both Master Label lookups instead, most
      // likely first, and keep the first answer that actually names a catalog product.
      const looksLikeBarcode = /^\d{12,}$/.test(query);
      const lookUpByBarcode = () =>
        ForgeApiService.fetchItemByBarcode(token, query, chainId).then((response) => ({
          ...EMPTY_DRAFT,
          kind: 'catalog_item' as const,
          normalizedBarcode: response.normalizedBarcode,
          itemNumber: response.itemNumber,
          productName: response.item?.productName ?? null,
        }));
      const lookUpByItemNumber = () =>
        ForgeApiService.fetchItem(token, query, chainId).then((item) => ({
          ...EMPTY_DRAFT,
          kind: 'catalog_item' as const,
          normalizedBarcode: item.normalizedUpc ?? null,
          itemNumber: item.itemNumber ?? query,
          productName: item.productName ?? null,
        }));

      const attempts = looksLikeBarcode
        ? [lookUpByBarcode, lookUpByItemNumber]
        : [lookUpByItemNumber, lookUpByBarcode];
      let resolved: VerifiedIdentityDraft | null = null;
      for (const attempt of attempts) {
        const candidate = await attempt().catch(() => null);
        if (candidate?.productName && candidate.itemNumber) {
          resolved = candidate;
          break;
        }
      }
      if (!resolved) {
        throw new Error('No catalog product matched that value.');
      }
      setLookup({ sampleId, query, result: resolved, error: null, isLoading: false });
    } catch (error) {
      setLookup({
        sampleId,
        query,
        result: null,
        error: error instanceof Error ? error.message : 'No catalog product matched that value.',
        isLoading: false,
      });
    }
  }, [activeLookup?.query, chainId, selectedSampleId, token]);

  const handleApplyLookup = useCallback(() => {
    const resolved = activeLookup?.result;
    if (!resolved) return;
    updateDraft((current) => ({
      ...current,
      kind: 'catalog_item',
      normalizedBarcode: resolved.normalizedBarcode,
      itemNumber: resolved.itemNumber,
      productName: resolved.productName,
    }));
    setLookup(null);
  }, [activeLookup?.result, updateDraft]);

  const handleSetStrategy = useCallback(
    async (strategy: RecognitionSamplingStrategy) => {
      if (!hasInternalAccess || !unit || unit.samplingStrategy === strategy) return;
      const targetUnitKey = unitKey(unit.imageId, unit.modTag);
      setIsStrategySaving(true);
      try {
        const response = await ForgeApiService.setRecognitionSamplingStrategy(token, chainId, {
          image_id: unit.imageId,
          mod_tag: unit.modTag,
          strategy,
        });
        // Tagging rewrites EVERY sample of the unit, so it spends every concurrency token this page
        // holds - not just the open polygon's. Patching only `samplingStrategy` would leave the
        // next verdict on any polygon here sending a spent token and losing the reviewer's draft to
        // a 409 they caused themselves.
        setUnit((current) =>
          current && unitKey(current.imageId, current.modTag) === targetUnitKey
            ? {
                ...current,
                samplingStrategy: strategy,
                samples: response.updatedAt
                  ? current.samples.map((sample) => ({
                      ...sample,
                      updatedAt: response.updatedAt ?? sample.updatedAt,
                    }))
                  : current.samples,
              }
            : current
        );
        setQueue((current) =>
          current.map((row) =>
            unitKey(row.imageId, row.modTag) === targetUnitKey
              ? { ...row, samplingStrategy: strategy }
              : row
          )
        );
        showSuccessToast(`Capture unit tagged ${SAMPLING_STRATEGY_LABELS[strategy].toLowerCase()}`);
        // No fresh token means every token on screen is knowingly stale; refetch rather than keep
        // one. `isStrategySaving` still disables the tabs, so no second tag races the reload.
        if (!response.updatedAt && openUnitKeyRef.current === targetUnitKey) await loadUnit();
      } catch (error) {
        showErrorToast(
          error instanceof Error ? error.message : 'Error updating the sampling strategy'
        );
      } finally {
        setIsStrategySaving(false);
      }
    },
    [chainId, hasInternalAccess, loadUnit, showErrorToast, showSuccessToast, token, unit]
  );

  // Page-level shortcuts, and the single owner of Escape for this page.
  //
  // Modal ordering lives here rather than in each overlay: sibling effects register their document
  // listeners in tree order, not in depth order, so an overlay that closes itself from its own
  // capture-phase listener wins over any overlay opened later regardless of which one is on top.
  // The bucket chooser owns the keyboard while it is open (it swallows every printable key from
  // capture phase) and the confidence popover stops Escape from reaching this bubble-phase
  // listener, so both close before the inspector panel does. Typing in any field always wins, so
  // nothing here fires from inside the note or lookup inputs.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isBucketChooserOpen) return;
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable
      ) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          selectSampleAt(selectedVisibleIndex + 1);
          return;
        case 'ArrowLeft':
          event.preventDefault();
          selectSampleAt(selectedVisibleIndex - 1);
          return;
        case '[':
          event.preventDefault();
          stepUnit(-1);
          return;
        case ']':
          event.preventDefault();
          stepUnit(1);
          return;
        case 'Enter':
          // A focused button or link already means Enter by activating itself; stealing it would
          // make tabbing to "Primary bucket (B)" and pressing Enter save the verdict instead.
          if (target?.tagName === 'BUTTON' || target?.tagName === 'A') return;
          event.preventDefault();
          void handleSave();
          return;
        default:
          break;
      }
      switch (event.key.toLowerCase()) {
        case 'c':
          event.preventDefault();
          handleConfirmScan();
          break;
        case 'n':
          event.preventDefault();
          handleMarkKind('non_product');
          break;
        case 'u':
          event.preventDefault();
          handleMarkKind('unresolved_product');
          break;
        case 'b':
          event.preventDefault();
          setIsBucketChooserOpen(true);
          break;
        case 'i':
          event.preventDefault();
          if (selectedSample) setIsPanelOpen(true);
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    handleConfirmScan,
    handleMarkKind,
    handleSave,
    isBucketChooserOpen,
    selectSampleAt,
    selectedVisibleIndex,
    selectedSample,
    stepUnit,
  ]);

  // Every sample in the selected type view gets a box, including unscoreable samples with no
  // prediction. Source indexes remain beside each box so the selected outline still maps back to
  // the complete unit while navigation stays inside the visible subset.
  const boxes = useMemo(
    () =>
      visibleSampleEntries.map(({ sample, sampleIndex }) =>
        toImageBox(
          sample,
          recognitionBoxVisual(sample),
          sampleIndex === selectedIndex,
          polygonStrokeWidth,
          overlayOpacityPct / 100
        )
      ),
    [overlayOpacityPct, polygonStrokeWidth, selectedIndex, visibleSampleEntries]
  );

  // Recomputed from the unit state, which `applySavedReview` updates in place, so the panel moves
  // the moment a verdict is stored rather than waiting for the unit to be refetched.
  const stats = useMemo(() => unitStats(samples), [samples]);
  const reviewedCount = stats.reviewed;
  const validationMessage = selectedSample ? reviewValidationMessage(selectedSample, draft) : null;
  const effectiveBuckets = selectedSample ? effectiveReviewBuckets(selectedSample, draft) : null;
  const activeQueueChips: Array<{ key: QueueFilterKey; value: string; clear: () => void }> = [];
  if (modTagFilter) {
    activeQueueChips.push({
      key: 'modTag',
      value: modTagFilter,
      clear: () => applyQueueFilter({ tag: null }),
    });
  }
  if (collectorFilter) {
    activeQueueChips.push({
      key: 'collector',
      value: collectorFilter,
      clear: () => applyQueueFilter({ collector: null }),
    });
  }
  if (capturedFilter) {
    activeQueueChips.push({
      key: 'capturedDate',
      value: capturedToFilter ? `${capturedFilter}–${capturedToFilter}` : capturedFilter,
      clear: () => {
        updateFilters({ captured: null, capturedTo: null, dateMode: 'week', page: 1 });
      },
    });
  }

  const renderQueueFilters = (): React.ReactElement => (
    <div className="rr-tool-queue-filters">
      <div className="rr-tool-queue-scope-row">
        <label className="dg-sort-control">
          <span>Chain</span>
          <select
            aria-label="Chain"
            value={chainId}
            onChange={(event) =>
              updateFilters({
                chain: Number(event.target.value),
                image: null,
                mod: null,
                sample: null,
                page: 1,
              })
            }
          >
            {FORGE_CHAIN_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <ReviewQueueDatePicker
          value={{
            mode: dateMode as ReviewQueueDateMode,
            weekStart: weekFilter ?? queueWeek ?? lastCompletedWeekStart() ?? '',
            capturedDate: capturedFilter,
            capturedDateTo: capturedToFilter,
          }}
          onChange={(selection) =>
            updateFilters({
              week: selection.weekStart,
              captured: selection.capturedDate,
              capturedTo: selection.capturedDateTo,
              dateMode: selection.mode,
              image: null,
              mod: null,
              sample: null,
              page: 1,
            })
          }
        />
        <label className="dg-sort-control">
          <span>Sort</span>
          <select
            aria-label="Sort review queue"
            value={queueSort}
            onChange={(event) =>
              updateFilters({
                sort: event.target.value as RecognitionReviewQueueSort,
                page: 1,
              })
            }
          >
            <option value="collected_desc">Collected % ↓</option>
            <option value="triage_asc">Review priority</option>
          </select>
        </label>
      </div>
      <form
        className="rr-tool-queue-filter-row"
        onSubmit={(event) => {
          event.preventDefault();
          applyQueueFilter({
            tag: modTagInput.trim() || null,
            collector: collectorInput.trim() || null,
          });
        }}
      >
        <label className="dg-sort-control">
          <span>Mod tag</span>
          <input
            type="search"
            aria-label="Filter the queue by mod tag"
            placeholder="Mod tag contains"
            value={modTagInput}
            onChange={(event) => setModTagInput(event.target.value)}
          />
        </label>
        <label className="dg-sort-control">
          <span>Collector</span>
          <input
            type="search"
            aria-label="Filter the queue by collector"
            placeholder="Email contains"
            value={collectorInput}
            onChange={(event) => setCollectorInput(event.target.value)}
          />
        </label>
        <button type="submit" className="dg-secondary-button">
          Apply
        </button>
      </form>
      {/* The backend, not this list, decides queue order and membership, so "hide reviewed" is a
          request parameter with a visible control rather than a local filter over the page. */}
      <label className="rr-tool-queue-toggle">
        <input
          type="checkbox"
          checked={hideReviewed}
          onChange={(event) =>
            updateFilters({ reviewed: event.target.checked ? 'pending' : 'all', page: 1 })
          }
        />
        <span>
          Hide fully reviewed units
          {hideReviewed && hiddenReviewedUnits !== null ? ` (${hiddenReviewedUnits} hidden)` : ''}
        </span>
      </label>
      {activeQueueChips.length > 0 ? (
        <div className="rr-tool-queue-chips" data-testid="rr-queue-active-filters">
          {activeQueueChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className="rr-tool-filter-chip"
              aria-label={`Clear the ${QUEUE_FILTER_LABELS[chip.key].toLowerCase()} filter`}
              onClick={chip.clear}
            >
              {QUEUE_FILTER_LABELS[chip.key]}: {chip.value}
              <CloseOutlinedIcon fontSize="inherit" />
            </button>
          ))}
          <button
            type="button"
            className="rr-tool-filter-chip is-clear"
            onClick={() =>
              updateFilters({
                tag: null,
                collector: null,
                captured: null,
                capturedTo: null,
                dateMode: 'week',
                page: 1,
              })
            }
          >
            Clear all
          </button>
        </div>
      ) : null}
      {droppedFilters.length > 0 ? (
        <div
          className="rr-tool-hint is-warning"
          role="status"
          data-testid="rr-queue-dropped-filter"
        >
          {droppedFilters.join(' and ')} falls outside the week starting{' '}
          {queueWeek ?? weekFilter ?? '-'}, so it was dropped; the queue below ignores it.
        </div>
      ) : null}
    </div>
  );

  const renderQueue = (): React.ReactElement => {
    if (isQueueLoading) return <div className="dg-loading-state">Loading review queue...</div>;
    if (queueError) {
      return (
        <div className="dg-empty-state">
          <strong>Review queue could not be loaded</strong>
          <span>{queueError}</span>
          <button type="button" className="dg-secondary-button" onClick={() => void loadQueue()}>
            Retry
          </button>
        </div>
      );
    }
    if (queue.length === 0) {
      return (
        <div className="dg-empty-state">
          <strong>Nothing to review</strong>
          <span>
            {hideReviewed
              ? 'No capture units are still awaiting review for this chain and week. Untick "Hide fully reviewed units" to see the ones already done.'
              : 'No capture units were queued for this chain and week.'}
          </span>
        </div>
      );
    }
    return (
      <ul className="rr-tool-queue-list">
        {queue.map((row) => {
          const key = unitKey(row.imageId, row.modTag);
          const isActive = imageId !== null && key === unitKey(imageId, modFilter);
          return (
            <li key={key}>
              <button
                type="button"
                data-testid={`rr-queue-unit-${key}`}
                className={`rr-tool-queue-row${isActive ? ' is-active' : ''}${
                  unitIsComplete(row) ? ' is-complete' : ''
                }`}
                onClick={() => openUnit(row)}
              >
                <span className="rr-tool-queue-title">
                  <span className="rr-tool-queue-mod">{row.modTag ?? 'No mod tag'}</span>
                  <span className="rr-tool-queue-image">#{row.imageId}</span>
                </span>
                <span className="rr-tool-queue-meta">
                  <span
                    className="rr-tool-queue-counts"
                    title="Reviewed | Collected | All"
                    aria-label={`${row.reviewed} reviewed, ${row.scanned} collected, ${row.total} all`}
                  >
                    <strong>{row.reviewed}</strong>
                    <span aria-hidden="true">|</span>
                    <strong>{row.scanned}</strong>
                    <span aria-hidden="true">|</span>
                    <strong>{row.total}</strong>
                  </span>
                  <span
                    className="rr-tool-queue-collector"
                    title={row.collectorEmails.join(', ') || 'No collector email'}
                  >
                    {row.collectorEmails.join(', ') || 'No collector email'}
                  </span>
                  <span
                    className={`dg-scope-pill ${
                      row.samplingStrategy === 'representative' ? 'is-full' : 'is-unreviewed'
                    }`}
                  >
                    {SAMPLING_STRATEGY_LABELS[row.samplingStrategy]}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  const renderStatsView = (
    title: string,
    description: string,
    view: RecognitionUnitOutcomeStats,
    testId: string
  ): React.ReactElement => (
    <div className="rr-tool-stats-view" data-testid={testId}>
      <div className="rr-tool-stats-view-head">
        <strong>{title}</strong>
        <span>{description}</span>
      </div>
      <OutcomeDistributionBar
        cells={view.cells}
        ariaLabel={`${title} outcome distribution`}
        height={10}
      />
      <dl className="rr-tool-stats-cells">
        {RECOGNITION_CELL_ORDER.map((cell) => (
          <div key={cell} className={`rr-tool-stats-cell is-${RECOGNITION_CELL_TONE[cell]}`}>
            <dt>{RECOGNITION_CELL_LABELS[cell]}</dt>
            <dd data-testid={`${testId}-${cell}`}>{view.cells[cell]}</dd>
          </div>
        ))}
      </dl>
      <div className="rr-tool-stats-footer">
        <span>{view.scoreable} scoreable</span>
        {/* Not a wrong answer: these are samples the evaluation could not score at all. */}
        <span title="Samples this view could not score at all">
          {view.coverageFailures} coverage failure{view.coverageFailures === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );

  const renderStats = (): React.ReactElement => {
    if (!unit) {
      return (
        <div className="dg-empty-state">
          <strong>No unit loaded</strong>
          <span>Unit statistics appear once a capture unit is open.</span>
        </div>
      );
    }
    return (
      <div className="rr-tool-stats-body">
        <div className="rr-tool-stats-metrics">
          <div className="cleaning-metric-tile">
            <div className="cleaning-metric-label">Reviewed</div>
            <div className="cleaning-metric-value" data-testid="rr-stats-reviewed">
              {stats.reviewed}/{stats.total}
            </div>
          </div>
          {/* "Collected" counts only a scan that resolved to a catalog item, exactly as the queue
              row does. A barcode that did not resolve is a scan issue, not a collected polygon. */}
          <div
            className="cleaning-metric-tile"
            title="Polygons whose scan resolved to a catalog item"
          >
            <div className="cleaning-metric-label">Collected of existing</div>
            <div className="cleaning-metric-value" data-testid="rr-stats-collected">
              {stats.scanned}/{stats.total}
            </div>
          </div>
          <div
            className="cleaning-metric-tile"
            title="Unresolved scans, plus reviewed polygons scored as a wrong scan"
          >
            <div className="cleaning-metric-label">Scan issues</div>
            <div className="cleaning-metric-value" data-testid="rr-stats-scan-issues">
              {stats.scanIssues}
            </div>
          </div>
        </div>
        {renderStatsView(
          'Reviewed',
          'Stored reviewer verdicts',
          stats.reviewedView,
          'rr-stats-reviewed-view'
        )}
        {renderStatsView('Raw', 'What production predicted', stats.raw, 'rr-stats-raw-view')}
      </div>
    );
  };

  const renderStage = (): React.ReactElement => {
    if (isUnitLoading) return <div className="dg-loading-state">Loading capture unit...</div>;
    if (unitError) {
      return (
        <div className="dg-empty-state">
          <strong>Capture unit could not be loaded</strong>
          <span>{unitError}</span>
          <button type="button" className="dg-secondary-button" onClick={() => void loadUnit()}>
            Retry
          </button>
        </div>
      );
    }
    if (!unit) {
      return (
        <div className="dg-empty-state">
          <strong>Pick a capture unit</strong>
          <span>Choose a unit from the queue to start reviewing its polygons.</span>
        </div>
      );
    }
    if (captureImage.status === 'loading') {
      return <div className="dg-loading-state">Loading capture image...</div>;
    }
    if (captureImage.status === 'missing') {
      return (
        <div className="dg-empty-state" data-testid="rr-stage-image-missing">
          <strong>No capture image</strong>
          <span>
            This capture unit came back without an image URI, so its polygons cannot be drawn.
            Reload the unit to ask for it again.
          </span>
          <button type="button" className="dg-secondary-button" onClick={() => void loadUnit()}>
            Retry
          </button>
        </div>
      );
    }
    if (captureImage.status === 'failed') {
      return (
        <div className="dg-empty-state" data-testid="rr-stage-image-failed">
          <strong>Capture image could not be loaded</strong>
          <span>The image URI did not load; a signed capture URL may have expired.</span>
          <button
            type="button"
            className="dg-secondary-button"
            onClick={() => setImageRetryCount((count) => count + 1)}
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      <div className="rr-tool-stage-image">
        <ImageViewer<BBox>
          bboxes={[]}
          image={captureImage.element}
          boxActions={[]}
          productRecognitionDetections={boxes}
          onBoxClick={() => undefined}
          onProductRecognitionClick={(_box, index) => {
            selectSampleAt(index, true);
          }}
          onBoxRightClick={(box) => {
            const index = boxes.indexOf(box as ImageBox);
            if (index >= 0) selectSampleAt(index, true);
          }}
          fitToContainerWidth
          imageBackgroundColor="var(--elevated)"
          showFrameBorder={false}
        />
      </div>
    );
  };

  const renderSelectedItemBlock = (sample: RecognitionReviewSampleDetail): React.ReactElement => (
    <div className="mdc-summary-section rr-tool-selected-item">
      <div className="mdc-summary-section-title">Selected item</div>
      {captureImage.status === 'ready' ? (
        <DetectionCropPreview
          imageElement={captureImage.element}
          crop={sampleShape(sample)}
          boxes={[
            toImageBox(
              sample,
              recognitionBoxVisual(sample),
              true,
              polygonStrokeWidth,
              overlayOpacityPct / 100
            ),
          ]}
          compact
        />
      ) : (
        <div className="mdc-detail-preview-empty">Preview unavailable</div>
      )}
      <div className="rr-tool-selected-item-meta">
        <span
          className={`rr-tool-detection-kind is-${
            sample.classificationType === 'carton' ? 'carton' : 'product'
          }`}
        >
          {sample.classificationType === 'carton' ? 'Carton' : 'Product'}
        </span>
        <span>
          Shelf {valueOrDash(sample.shelfNumber)} · column {valueOrDash(sample.columnNumber)}
        </span>
      </div>
    </div>
  );

  const renderIdentityBlock = (sample: RecognitionReviewSampleDetail): React.ReactElement => (
    <div className="mdc-summary-section">
      <div className="mdc-summary-section-title">Submitted identity</div>
      <SummaryRow
        label="Status"
        value={SUBMITTED_IDENTITY_STATUS_LABELS[sample.submittedIdentity.status]}
      />
      <SummaryRow label="Raw barcode" value={sample.submittedIdentity.rawBarcode} mono />
      <SummaryRow
        label="Normalized barcode"
        value={sample.submittedIdentity.normalizedBarcode}
        mono
      />
      <SummaryRow label="Item number" value={sample.submittedIdentity.itemNumber} mono />
      <SummaryRow label="Product" value={sample.submittedIdentity.productName} />
      <SummaryRow label="Collector" value={sample.submittedIdentity.submittedByEmail} />
      {sample.submittedIdentity.status === 'scan_unresolved' ? (
        <div className="rr-tool-hint is-warning">
          The submitted barcode above did not resolve to a catalog item. Look the correct identity
          up rather than confirming the scan.
        </div>
      ) : null}
      {sample.review.status === 'reviewed' ? (
        <>
          <div className="mdc-summary-section-title">Verified identity</div>
          <SummaryRow
            label="Kind"
            value={
              sample.review.verifiedKind
                ? GROUND_TRUTH_KIND_LABELS[sample.review.verifiedKind]
                : null
            }
          />
          <SummaryRow label="Item number" value={sample.review.verifiedItemNumber} mono />
          <SummaryRow label="Product" value={sample.review.verifiedProductName} />
          <SummaryRow
            label="Reviewer"
            value={sample.review.reviewedByEmail ?? (sample.review.reviewedByYou ? 'you' : null)}
          />
        </>
      ) : null}
    </div>
  );

  const renderPredictionBlock = (sample: RecognitionReviewSampleDetail): React.ReactElement => {
    const { confidence, candidates } = sample.recognition;
    return (
      <div className="mdc-summary-section">
        <div className="mdc-summary-section-title">Prediction</div>
        <div className="rr-tool-verdict">
          <span
            className={`dg-scope-pill ${confidence.confident ? 'is-full' : 'is-partial'}`}
            title={`Confidence profile ${confidence.profileVersion}`}
          >
            {confidence.confident ? 'Confident' : 'Unconfident'}
          </span>
          <span className="rr-tool-verdict-detail">
            C = {formatSimilarity(confidence.value)} · s1 {formatSimilarity(confidence.s1)} · s2{' '}
            {formatSimilarity(confidence.s2)} · effective Δ(s1−s2){' '}
            {confidence.s1 === null || confidence.s2 === null
              ? '-'
              : formatSimilarity(confidence.s1 - confidence.s2)}
            {confidence.runnerUpFloorUsed ? ' (runner-up floor)' : ''} ·{' '}
            {RECOGNITION_CONFIDENCE_PROFILE_LABEL} {confidence.profileVersion}
          </span>
        </div>
        {candidates.length === 0 ? (
          <div className="rr-tool-hint">
            No Recognition candidates were captured for this polygon.
          </div>
        ) : (
          <ul className="rr-tool-candidates">
            {candidates.slice(0, 3).map((candidate) => (
              <li key={`${candidate.rank}-${candidate.normalizedBarcode ?? candidate.itemNumber}`}>
                <div className="rr-tool-candidate-head">
                  <span className="rr-tool-candidate-rank">#{candidate.rank}</span>
                  <span className="rr-tool-candidate-name">
                    {valueOrDash(candidate.productName)}
                  </span>
                  <span className="rr-tool-candidate-score">
                    {formatSimilarity(candidate.similarity)}
                  </span>
                </div>
                <div className="rr-tool-candidate-ids">
                  <span>{valueOrDash(candidate.itemNumber)}</span>
                  <span>{valueOrDash(candidate.normalizedBarcode)}</span>
                </div>
                <GroupLinkChips candidate={candidate} />
                {candidate.referenceCrops.length > 0 ? (
                  <div className="rr-tool-ref-crops">
                    {candidate.referenceCrops.slice(0, 4).map((crop) => (
                      <MasterLabelLink
                        key={crop.cropId}
                        to={buildMasterLabelUrl({
                          chainId,
                          normalizedBarcode: candidate.normalizedBarcode ?? undefined,
                          itemNumber: candidate.itemNumber ?? undefined,
                          cropIds: [crop.cropId],
                        })}
                        className="rr-tool-ref-crop"
                        title={`Open crop ${crop.cropId} in Master Label`}
                      >
                        {crop.vizUrl ? (
                          <img src={crop.vizUrl} alt={`Reference crop ${crop.cropId}`} />
                        ) : (
                          <span className="rr-tool-ref-crop-empty">no image</span>
                        )}
                      </MasterLabelLink>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        <div className="rr-tool-hint">
          Inference-time groups control the metric. Current groups are shown only to expose drift.
        </div>
      </div>
    );
  };

  const renderEvidenceBlock = (sample: RecognitionReviewSampleDetail): React.ReactElement => {
    const { evidence } = sample;
    const isNewProduct =
      evidence.productAgeDays !== null && evidence.productAgeDays < NEW_PRODUCT_AGE_DAYS;
    return (
      <div className="mdc-summary-section">
        <div className="mdc-summary-section-title">Evidence</div>
        <SummaryRow label="Truth item number" value={evidence.truthItemNumber} mono />
        <SummaryRow label="Total active crops" value={evidence.totalActiveCrops} />
        <SummaryRow
          label="Product age"
          value={evidence.productAgeDays === null ? null : `${evidence.productAgeDays} days`}
        />
        {isNewProduct ? (
          <div className="rr-tool-hint is-warning">
            New product (&lt;{NEW_PRODUCT_AGE_DAYS}d) - collection may not have caught up yet.
          </div>
        ) : null}
        {evidence.embeddingCounts.length === 0 ? (
          <div className="rr-tool-hint">No embedding crops recorded for the truth identity.</div>
        ) : (
          <table className="dg-group-table rr-tool-evidence-table" aria-label="Embedding crops">
            <thead>
              <tr>
                <th>Design</th>
                <th>Orientation</th>
                <th>Active</th>
                <th>Inactive</th>
              </tr>
            </thead>
            <tbody>
              {evidence.embeddingCounts.map((count) => (
                <tr key={`${count.designId ?? 'none'}-${count.orientation}`}>
                  <td>{valueOrDash(count.designId)}</td>
                  <td>{valueOrDash(count.orientation)}</td>
                  <td>{count.activeCount}</td>
                  <td>{count.inactiveCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderActionsBlock = (sample: RecognitionReviewSampleDetail): React.ReactElement => (
    <div className="mdc-summary-section">
      <div className="mdc-summary-section-title">Verdict</div>
      <div className="rr-tool-actions">
        <button
          type="button"
          className="dg-primary-button"
          disabled={sample.submittedIdentity.status !== 'scanned'}
          onClick={handleConfirmScan}
        >
          Confirm scan (C)
        </button>
        <button
          type="button"
          className="dg-secondary-button"
          onClick={() => handleMarkKind('non_product')}
        >
          Non-product (N)
        </button>
        <button
          type="button"
          className="dg-secondary-button"
          onClick={() => handleMarkKind('unresolved_product')}
        >
          Unresolved product (U)
        </button>
      </div>
      <div className="rr-tool-lookup">
        <label className="dg-sort-control">
          <span>Correct identity</span>
          <input
            type="text"
            aria-label="Item number or barcode"
            placeholder="Item number or barcode"
            value={activeLookup?.query ?? ''}
            onChange={(event) =>
              setLookup({
                sampleId: sample.evaluationSampleId,
                query: event.target.value,
                result: null,
                error: null,
                isLoading: false,
              })
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleLookup();
              }
            }}
          />
        </label>
        <button
          type="button"
          className="dg-secondary-button"
          disabled={activeLookup?.isLoading || !(activeLookup?.query ?? '').trim()}
          onClick={() => void handleLookup()}
        >
          {activeLookup?.isLoading ? 'Looking up...' : 'Look up'}
        </button>
      </div>
      {activeLookup?.error ? (
        <div className="rr-tool-hint is-danger" role="alert">
          {activeLookup.error}
        </div>
      ) : null}
      {activeLookup?.result ? (
        <div className="rr-tool-lookup-result" data-testid="rr-lookup-result">
          <div>
            <strong>{valueOrDash(activeLookup.result.productName)}</strong>
            <span>
              {valueOrDash(activeLookup.result.itemNumber)} ·{' '}
              {valueOrDash(activeLookup.result.normalizedBarcode)}
            </span>
          </div>
          <button type="button" className="dg-primary-button" onClick={handleApplyLookup}>
            Use this identity
          </button>
        </div>
      ) : null}
      <div className="rr-tool-draft" data-testid="rr-draft-summary">
        <SummaryRow
          label="Verified kind"
          value={draft.kind ? GROUND_TRUTH_KIND_LABELS[draft.kind] : null}
        />
        <SummaryRow label="Verified product" value={draft.productName} />
        <SummaryRow label="Verified item number" value={draft.itemNumber} mono />
      </div>
      <div className="rr-tool-bucket-row">
        <button
          type="button"
          className="dg-secondary-button"
          onClick={() => setIsBucketChooserOpen(true)}
        >
          Primary bucket (B)
        </button>
        <span className="rr-tool-bucket-value">
          {effectiveBuckets?.primaryBucket ? bucketLabel(effectiveBuckets.primaryBucket) : 'None'}
        </span>
      </div>
      {effectiveBuckets && effectiveBuckets.dropped.length > 0 ? (
        <div className="rr-tool-hint" role="status" data-testid="rr-buckets-dropped">
          {effectiveBuckets.dropped.map(bucketLabel).join(', ')}{' '}
          {effectiveBuckets.dropped.length === 1 ? 'is' : 'are'} not saved with this verdict: a
          confident correct recognition carries no issue bucket.
        </div>
      ) : null}
      <label className="rr-tool-note">
        <span>Note</span>
        <textarea
          aria-label="Review note"
          rows={2}
          value={draft.note}
          onChange={(event) => updateDraft((current) => ({ ...current, note: event.target.value }))}
        />
      </label>
      {validationMessage ? (
        <div className="rr-tool-hint is-warning" role="status">
          {validationMessage}
        </div>
      ) : null}
      <button
        type="button"
        className="dg-primary-button rr-tool-save"
        disabled={!hasInternalAccess || isSaving || Boolean(validationMessage)}
        onClick={() => void handleSave()}
      >
        {isSaving ? 'Saving...' : 'Save review (Enter)'}
      </button>
    </div>
  );

  return (
    <div className="page-content page-content--wide rr-tool-page">
      <div className="forge-card rr-tool-hero">
        <div className="rr-tool-hero-bar">
          <div>
            <div className="rr-tool-eyebrow">Labeling tools · Recognition quality</div>
            <h1>Recognition Review</h1>
            <div className="rr-tool-copy">
              Verify the ground-truth identity of each detected polygon so the weekly Confident
              correct rate is measured against reviewed truth.
            </div>
            <div className="rr-tool-crosslink">
              <Link to={FORGE_RECOGNITION_OKRS_PATH}>Recognition OKRs (reviewed view) ↗</Link>
            </div>
          </div>
          <div className="rr-tool-controls">
            <ConfidenceMatrixInfo />
          </div>
        </div>
        <div className="rr-tool-shortcuts">
          <span>
            <kbd>C</kbd> confirm scan
          </span>
          <span>
            <kbd>N</kbd> non-product
          </span>
          <span>
            <kbd>U</kbd> unresolved
          </span>
          <span>
            <kbd>B</kbd> bucket
          </span>
          <span>
            <kbd>Enter</kbd> save
          </span>
          <span>
            <kbd>←</kbd>
            <kbd>→</kbd> polygon
          </span>
          <span>
            <kbd>[</kbd>
            <kbd>]</kbd> unit
          </span>
          <span>
            <kbd>I</kbd> details
          </span>
          <span>
            <kbd>Esc</kbd> close details
          </span>
        </div>
      </div>

      <div className="rr-tool-workspace">
        <aside className="forge-card rr-tool-queue" aria-label="Recognition review queue">
          <div className="rr-tool-queue-head">
            <div className="forge-card-title">
              <span>Review queue</span>
              <span className="rr-tool-queue-count-legend">Reviewed | Collected | All</span>
            </div>
            {renderQueueFilters()}
          </div>
          {renderQueue()}
          <div className="dg-pagination rr-tool-queue-pagination">
            <button
              type="button"
              className="dg-icon-button"
              aria-label="Previous page"
              disabled={page <= 1 || isQueueLoading}
              onClick={() => updateFilters({ page: Math.max(1, page - 1) })}
            >
              <ChevronLeftOutlinedIcon />
            </button>
            <span>
              Page {page} · {queueTotal} unit{queueTotal === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              className="dg-icon-button"
              aria-label="Next page"
              disabled={isQueueLoading || page * QUEUE_PAGE_LIMIT >= queueTotal}
              onClick={() => updateFilters({ page: page + 1 })}
            >
              <ChevronRightOutlinedIcon />
            </button>
          </div>
        </aside>

        <section className="forge-card rr-tool-stage">
          <div className="rr-tool-stage-header">
            <div>
              <div className="rr-tool-eyebrow">Capture unit</div>
              <h2>
                {unit
                  ? `${unit.modTag ?? 'No mod tag'} · image #${unit.imageId}`
                  : 'No unit loaded'}
              </h2>
              <div className="rr-tool-stage-meta">
                {unit ? (
                  <>
                    <span>{captureUnitStoreLabel(unit.storeName, unit.storeId)}</span>
                    <span>
                      {reviewedCount}/{samples.length} polygons reviewed
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            <div className="rr-tool-stage-actions">
              <button
                type="button"
                className="dg-secondary-button"
                disabled={!selectedSample}
                onClick={() => setIsPanelOpen(true)}
              >
                Details (I)
              </button>
              <div
                className="mdc-stage-tabs mdc-detection-type-tabs rr-tool-detection-type-tabs"
                role="group"
                aria-label="Detection type visibility"
              >
                {(['product', 'carton', 'all'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={detectionVisibility === type ? 'is-active' : ''}
                    aria-pressed={detectionVisibility === type}
                    onClick={() => {
                      setDetectionVisibility(type);
                      const selectedIsVisible =
                        type === 'all' || selectedSample?.classificationType === type;
                      if (!selectedIsVisible) {
                        const nextSample = samples.find(
                          (sample) => sample.classificationType === type
                        );
                        updateFilters({ sample: nextSample?.evaluationSampleId ?? null });
                        if (!nextSample) setIsPanelOpen(false);
                      }
                    }}
                  >
                    {type === 'all' ? 'All' : type[0].toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
              <div
                className="rr-tool-detection-type-legend"
                aria-label="Detection type border legend"
              >
                <span>
                  <span className="rr-tool-detection-type-marker is-product" aria-hidden="true">
                    P
                  </span>
                  Product
                </span>
                <span>
                  <span className="rr-tool-detection-type-marker is-carton" aria-hidden="true">
                    C
                  </span>
                  Carton
                </span>
              </div>
              <div className="rr-tool-render-controls">
                <div className="mdc-range-control">
                  <div className="mdc-range-header">
                    <span className="mdc-range-label">
                      <span className="mdc-range-label-full">Border thickness</span>
                      <span className="mdc-range-label-short">Border</span>
                    </span>
                    <span className="mdc-value-pill">{polygonStrokeWidth}px</span>
                  </div>
                  <input
                    aria-label="Polygon border"
                    className="mdc-range-input"
                    type="range"
                    min={POLYGON_STROKE_RANGE.min}
                    max={POLYGON_STROKE_RANGE.max}
                    value={polygonStrokeWidth}
                    onChange={(event) => setPolygonStrokeWidth(Number(event.target.value))}
                  />
                </div>
                <div className="mdc-range-control">
                  <div className="mdc-range-header">
                    <span className="mdc-range-label">
                      <span className="mdc-range-label-full">Polygon fill</span>
                      <span className="mdc-range-label-short">Fill</span>
                    </span>
                    <span className="mdc-value-pill">{overlayOpacityPct}%</span>
                  </div>
                  <input
                    aria-label="Polygon fill"
                    className="mdc-range-input"
                    type="range"
                    min={OVERLAY_OPACITY_RANGE.min}
                    max={OVERLAY_OPACITY_RANGE.max}
                    value={overlayOpacityPct}
                    onChange={(event) => setOverlayOpacityPct(Number(event.target.value))}
                  />
                </div>
              </div>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Previous capture unit"
                disabled={nextQueueIndex(queueIndex, -1, queue.length) === null}
                onClick={() => stepUnit(-1)}
              >
                <ChevronLeftOutlinedIcon />
              </button>
              <button
                type="button"
                className="dg-icon-button"
                aria-label="Next capture unit"
                disabled={nextQueueIndex(queueIndex, 1, queue.length) === null}
                onClick={() => stepUnit(1)}
              >
                <ChevronRightOutlinedIcon />
              </button>
              <div className="dg-scope-tabs" role="group" aria-label="Sampling strategy">
                {SETTABLE_SAMPLING_STRATEGIES.map((strategy) => (
                  <button
                    key={strategy}
                    type="button"
                    className={unit?.samplingStrategy === strategy ? 'is-active' : ''}
                    aria-pressed={unit?.samplingStrategy === strategy}
                    disabled={!unit || !hasInternalAccess || isStrategySaving}
                    onClick={() => void handleSetStrategy(strategy)}
                  >
                    {SAMPLING_STRATEGY_LABELS[strategy]}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {renderStage()}
        </section>

        <aside className="forge-card rr-tool-stats" aria-label="Unit statistics">
          {/* No confidence-info control here: the hero already carries the one popover, and a
              second instance would duplicate its id. */}
          <div className="forge-card-title">
            <span>Unit statistics</span>
          </div>
          {renderStats()}
        </aside>
      </div>

      {selectedSample && isPanelOpen ? (
        <RecognitionInspectorModal
          title={`Polygon ${selectedIndex + 1} of ${samples.length}`}
          isBlocked={isBucketChooserOpen}
          onClose={() => setIsPanelOpen(false)}
        >
          <div className="rr-tool-inspector-column">
            {renderSelectedItemBlock(selectedSample)}
            {renderIdentityBlock(selectedSample)}
            {renderEvidenceBlock(selectedSample)}
          </div>
          <div className="rr-tool-inspector-column">{renderPredictionBlock(selectedSample)}</div>
          <div className="rr-tool-inspector-column">{renderActionsBlock(selectedSample)}</div>
        </RecognitionInspectorModal>
      ) : null}

      {isBucketChooserOpen ? (
        <BucketChooserOverlay
          selectedPath={draft.primaryBucket}
          onSelect={(path) => {
            updateDraft((current) => ({ ...current, primaryBucket: path }));
            setIsBucketChooserOpen(false);
          }}
          onClose={() => setIsBucketChooserOpen(false)}
        />
      ) : null}
    </div>
  );
};

/**
 * The polygon inspector, as a Remote DC-style modal.
 *
 * It owns Escape from the CAPTURE phase, the way Remote DC's detail panels do, so it closes before
 * the page-level shortcut handler sees the key. `isBlocked` is what keeps the bucket chooser
 * winning: sibling capture listeners fire in REGISTRATION order, and this modal is always mounted
 * before the chooser opens inside it, so without the guard the shallower overlay would swallow the
 * deeper one's Escape. The confidence popover stops Escape on its own for the same reason.
 *
 * `aria-modal="true"` claims the rest of the page is inert, so the modal has to make that true:
 * focus moves in on open, Tab cycles inside it, and the previously focused control gets it back on
 * close. Without that a keyboard reviewer would tab through the queue behind the verdict controls.
 */
function RecognitionInspectorModal({
  title,
  isBlocked,
  onClose,
  children,
}: {
  title: string;
  /** A deeper overlay is open and owns the keyboard; leave Escape alone. */
  isBlocked: boolean;
  onClose: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isBlocked) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [
        ...(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []),
      ].filter((element) => !element.hasAttribute('disabled'));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      // Tab off either end wraps back inside rather than escaping to the page the dialog covers.
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === dialogRef.current)) {
        event.preventDefault();
        last.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [isBlocked, onClose]);

  return (
    <div className="mdc-modal-backdrop" data-testid="rr-inspector-backdrop" onClick={onClose}>
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="mdc-modal rr-tool-inspector-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="mdc-modal-header">
          <div>
            <h2>{title}</h2>
          </div>
          <div className="mdc-modal-header-actions">
            <button
              type="button"
              className="mdc-modal-close"
              aria-label="Close details"
              onClick={onClose}
            >
              <CloseOutlinedIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        </header>
        <div className="rr-tool-inspector-body">{children}</div>
      </div>
    </div>
  );
}

export default RecognitionReviewToolPage;
